// [2026-09-02] ADDED — the platform at a glance: every tenant, all traffic, all money.
//
// ONE REQUEST (GET /admin/v1/overview) feeds every panel. The endpoint gathers its reads
// concurrently server-side, so this screen either has its data or is still loading — it never paints
// four populated charts beside five empty ones, which reads as broken rather than slow.
//
// THE PRICING AND BILLING FLAGS ARE SHOWN, not assumed. "Why was this customer refused?" is usually
// answered by billing_enabled or the session price, and an operator should not have to read config
// to find that out.

import {
  Card, Empty, Notice, Skeleton, Tile, num, ms, pct, useAsync,
} from '@incruiter/proctoring-client-frontend'
import { TrafficChart, LatencyTrend } from '@incruiter/proctoring-client-frontend'
import { Badge } from '@incruiter/proctoring-client-frontend/src/components/ui/badge.jsx'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@incruiter/proctoring-client-frontend/src/components/ui/table.jsx'
import { admin } from '../api.js'

const SEVERITY_VARIANT = { high: 'critical', medium: 'serious', low: 'degraded' }

export default function Overview({ days = 30, onError, onOpenClient }) {
  const { data, error, loading } = useAsync(() => admin.overview(days), [days])

  if (error) { onError?.(error); return <Notice kind="error">{error.message}</Notice> }
  if (loading || !data) return <Skeleton height={320} />

  const t = data.tiles || {}
  const pricing = data.pricing || {}
  const events = data.event_breakdown || { by_type: {}, by_severity: {} }
  const byType = Object.entries(events.by_type || {}).sort((a, b) => b[1] - a[1]).slice(0, 8)
  const bySeverity = Object.entries(events.by_severity || {}).sort((a, b) => b[1] - a[1])
  const top = data.top_clients || []

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* Billing off is the single most consequential flag on this screen: with it off, nothing is
          ever refused for credits and the money numbers below are informational only. Saying so
          beats an operator inferring it from a balance that never moves. */}
      {pricing.credits_enabled === false && (
        <Notice kind="warn">
          Credits are DISABLED in this environment — balances are recorded but nothing is refused.
        </Notice>
      )}

      <div style={{ display: 'grid', gap: 12,
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        <Tile label="Tenants" value={num(t.clients_total || 0)}
              sub={`${num(t.clients_active || 0)} active · ${num(t.clients_suspended || 0)} suspended`} />
        <Tile label={`API calls (${data.window_days}d)`} value={num(t.requests_total || 0)} />
        <Tile label="Error rate" value={pct(t.error_rate || 0)}
              tone={(t.error_rate || 0) > 0.05 ? 'var(--critical)' : undefined}
              sub={`${num(t.requests_failed || 0)} failed`} />
        <Tile label="Avg latency"
              value={t.avg_latency_ms == null ? '—' : ms(t.avg_latency_ms)} />
        <Tile label="Sessions" value={num(t.sessions_total || 0)}
              sub={`${num(t.events_total || 0)} events`} />
        <Tile label="Credits outstanding"
              value={num(t.credits_outstanding || 0, { decimals: 2 })}
              sub={`${num(t.credits_granted || 0, { decimals: 0 })} granted`} />
      </div>

      <Card title="Traffic" subtitle={`Every tenant, per day over ${data.window_days} days`}>
        <TrafficChart data={data.requests_series || []} />
      </Card>

      <Card title="Latency trend" subtitle="Average and p95 per day, platform-wide">
        <LatencyTrend data={data.latency_series || []} />
      </Card>

      <Card title="Busiest tenants"
            subtitle="Click a row to open the tenant"
            actions={t.busiest_endpoint
              ? <span className="muted" style={{ fontSize: 12 }}>
                  busiest endpoint: <span className="mono">{t.busiest_endpoint}</span>
                </span>
              : null}>
        {top.length === 0 ? (
          <Empty title="No traffic yet" hint="Tenants appear here once they start calling the API." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead style={{ textAlign: 'right' }}>Requests</TableHead>
                  <TableHead style={{ textAlign: 'right' }}>Error rate</TableHead>
                  <TableHead style={{ textAlign: 'right' }}>Sessions</TableHead>
                  <TableHead style={{ textAlign: 'right' }}>Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {top.map((c) => (
                  <TableRow key={c.client_id} style={{ cursor: 'pointer' }}
                            onClick={() => onOpenClient?.(c.client_id)}>
                    <TableCell>
                      <div>{c.name || c.client_id}</div>
                      <div className="muted mono" style={{ fontSize: 11.5 }}>{c.client_id}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={c.status === 'active' ? 'healthy' : 'critical'}>{c.status}</Badge>
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>{num(c.requests || 0)}</TableCell>
                    <TableCell style={{ textAlign: 'right',
                                        color: (c.error_rate || 0) > 0.05 ? 'var(--critical)' : undefined }}>
                      {pct(c.error_rate || 0)}
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>{num(c.sessions || 0)}</TableCell>
                    <TableCell style={{ textAlign: 'right',
                                        color: c.low_balance ? 'var(--critical)' : undefined }}>
                      {num(c.credits_balance || 0, { decimals: 2 })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <div style={{ display: 'grid', gap: 16,
                    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        <Card title="Candidate events by type" subtitle="Across every tenant">
          {byType.length === 0 ? <Empty title="No events yet" hint="" /> : (
            <div style={{ display: 'grid', gap: 8 }}>
              {byType.map(([type, n]) => {
                const total = byType.reduce((s, [, v]) => s + v, 0)
                return (
                  <div key={type} style={{ display: 'grid', gridTemplateColumns: '1fr auto',
                                           gap: 6, alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 13 }}>{type.replace(/_/g, ' ')}</div>
                      <div style={{ height: 6, borderRadius: 999, marginTop: 4,
                                    background: 'var(--surface-sunken)', overflow: 'hidden' }}>
                        <div style={{ width: `${total ? (n / total) * 100 : 0}%`, height: '100%',
                                      background: 'var(--series-1, #4c8dff)' }} />
                      </div>
                    </div>
                    <div className="mono" style={{ fontSize: 13 }}>{num(n)}</div>
                  </div>
                )
              })}
            </div>
          )}
        </Card>

        <Card title="Environment" subtitle="What this deployment is actually running with">
          <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
            {[
              ['Pricing mode', pricing.mode],
              ['Per session', `${pricing.browser_session_credits} credits`],
              ['Billing enabled', String(pricing.billing_enabled)],
              ['Credits enabled', String(pricing.credits_enabled)],
              ['Overdraft allowed', String(pricing.overdraft_allowed)],
              ['Low-balance threshold', String(pricing.low_balance_threshold)],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'grid', gridTemplateColumns: '190px 1fr', gap: 8 }}>
                <span className="muted">{k}</span><span className="mono">{v ?? '—'}</span>
              </div>
            ))}
          </div>
          {bySeverity.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
              {bySeverity.map(([sev, n]) => (
                <Badge key={sev} variant={SEVERITY_VARIANT[sev] || 'idle'}>{sev}: {num(n)}</Badge>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}
